/**
 * STAR TOPOLOGY DIAGNOSTICS — tier-aware instrumentation.
 *
 * Thread congestion on Tier 1 peers (Firefox, low-RAM Chrome) is part of the
 * symptom being investigated, so these helpers are designed to be cheap on
 * constrained peers:
 *
 *   - Tier 1: summary-only logs, no per-track spam, no JSON.stringify on hot paths
 *   - Tier 2+: full structured logs
 *
 * Every log line here carries the `[STAR_DIAG]` prefix so it can be filtered
 * from captured console exports trivially:
 *   grep '\[STAR_DIAG\]' console-export.txt
 *
 * Enable/disable via localStorage flag `moby_pref_starDiagnostics`.
 * Default ON during Phase 1 stabilization; flip to 'false' for production-quiet.
 */

import { getTransferResourceProfile } from './resource-tier';

const DIAG_FLAG = 'moby_pref_starDiagnostics';

export function isStarDiagnosticsEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem(DIAG_FLAG) !== 'false';
}

function tier(): 1 | 2 | 3 {
  try {
    return getTransferResourceProfile().tier;
  } catch {
    return 2;
  }
}

/**
 * Log a verbose diagnostic event — skipped entirely on Tier 1.
 * Use for per-track, per-frame, or per-message events where Tier 1 would choke.
 */
export function diagVerbose(tag: string, payload: Record<string, unknown>): void {
  if (!isStarDiagnosticsEnabled()) return;
  if (tier() === 1) return;
  // eslint-disable-next-line no-console
  console.log(`[STAR_DIAG] ${tag}`, payload);
}

/**
 * Log a summary diagnostic event — runs on every tier but payload-light.
 * Use for session lifecycle, promotion, hub departure.
 */
export function diagSummary(tag: string, payload: Record<string, unknown>): void {
  if (!isStarDiagnosticsEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[STAR_DIAG] ${tag}`, payload);
}

/**
 * Log a hot-path sample at a fixed rate (default 1 in 10). Cheap on Tier 1.
 * Ensures we still see something on constrained peers without blowing the log budget.
 */
const sampleCounters = new Map<string, number>();
export function diagSample(tag: string, rate: number, payload: () => Record<string, unknown>): void {
  if (!isStarDiagnosticsEnabled()) return;
  const count = (sampleCounters.get(tag) ?? 0) + 1;
  sampleCounters.set(tag, count);
  if (count % rate !== 0) return;
  // eslint-disable-next-line no-console
  console.log(`[STAR_DIAG] ${tag}`, { sampleN: count, ...payload() });
}

/**
 * Describe a session in a render-debug-friendly way. No track iteration
 * (which would walk all transceivers — expensive on large groups).
 */
export interface SessionRenderSnapshot {
  id: string;            // sessionId.slice(0,8)
  state: string;
  isGroupCall: boolean;
  groupAddress: string | null;
  initiator: string | null;
  treeRole: string | null;
  hubAddress: string | null;
  peerCount: number;
  remoteStreamCount: number;
  hasLocalStream: boolean;
}

export function snapshotSession(session: {
  sessionId: string;
  state: string | number;
  isGroupCall: boolean;
  groupAddress?: string;
  initiatorAddress?: string;
  treeRole?: string;
  hubAddress?: string;
  peerAddresses: string[];
  remoteStreams: Map<string, unknown>;
  localStream: unknown | null;
}): SessionRenderSnapshot {
  return {
    id: session.sessionId.slice(0, 8),
    state: String(session.state),
    isGroupCall: session.isGroupCall,
    groupAddress: session.groupAddress ? session.groupAddress.slice(0, 8) : null,
    initiator: session.initiatorAddress ? session.initiatorAddress.slice(0, 8) : null,
    treeRole: session.treeRole ?? null,
    hubAddress: session.hubAddress ? session.hubAddress.slice(0, 8) : null,
    peerCount: session.peerAddresses.length,
    remoteStreamCount: session.remoteStreams.size,
    hasLocalStream: session.localStream !== null,
  };
}

/**
 * Main-thread task-duration probe. Wrap a synchronous critical section to
 * measure how long it blocked the event loop. Logs only if duration exceeds
 * `warnMs` (default 16ms — one frame budget).
 *
 * Example:
 *   probe('hub-forward', () => forwardTracks(...), 8);
 */
export function probe<T>(tag: string, fn: () => T, warnMs: number = 16): T {
  if (!isStarDiagnosticsEnabled()) return fn();
  const start = performance.now();
  try {
    return fn();
  } finally {
    const duration = performance.now() - start;
    if (duration >= warnMs) {
      // eslint-disable-next-line no-console
      console.warn(`[STAR_DIAG] slow-task ${tag}`, {
        ms: Math.round(duration * 10) / 10,
        tier: tier(),
      });
    }
  }
}
